import React from 'react'

import PocketSuiteIcon from '../../icons/pocketsuite-icon-blue.svg?react'
import ZoomIcon from '../../icons/zoom.svg?react'
import { PSCredential } from '../../models/PSCredential'
import { RecordFactory } from '../../models/RecordFactory'
import { apiClient } from '../../modules/apiclient'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../app/design/Table'
import Tag from '../app/design/Tag'
import { ListHeader } from '../common/ListHeader'
import { Select } from '../common/fields/Select'

const viewWillAppear = async () => {
    const credential = (await RecordFactory.getInstance().loadCredentials('zoom')).at(0)
    return { credential }
}

function VideoConferenceStatus({ credential }: { credential: PSCredential | undefined }) {
    if (!credential || credential?.failingDate !== undefined) {
        return <Tag label="Disconnected" intent="danger" className="w-fit" size="sm" />
    }

    return <Tag label="Connected" intent="success" className="w-fit" size="sm" />
}

const render = ({
    base: { showProgressBar, hideProgressBar, logAndDisplayError },
    data: { credential },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const startZoomAuth = async () => {
        try {
            const resp = await apiClient.get('/auth/zoom')
            window.location.href = resp.data.url
        }
        catch (error) {
            logAndDisplayError(error)
        }
    }

    const disconnectAuth = async (credential: PSCredential | undefined) => {
        if (!credential) {
            return
        }

        showProgressBar()

        await RecordFactory.getInstance().cancelCredential(credential)

        hideProgressBar()
    }

    return (
        <div className="relative h-screen w-full overflow-y-auto p-8 pt-6">
            <ListHeader
                title="Video conferencing"
                backButton={{
                    label: 'Settings',
                    href: '/settings',
                }}
            />

            <div className="mt-6 rounded-lg bg-white p-8">
                <Table>
                    <TableHeader>
                        <TableRow location="head">
                            <TableHead>Account</TableHead>
                            <TableHead className="font-normal">Status</TableHead>
                            <TableHead className="font-normal"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <div className="flex items-center gap-x-4 font-bold">
                                    <ZoomIcon />
                                    <div>
                                        <Paragraph size="sm">
                                            {credential?.email || 'Google Calendar'}
                                        </Paragraph>
                                        <Paragraph size="sm" className="text-gray-500">
                                            Zoom
                                        </Paragraph>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <VideoConferenceStatus credential={credential} />
                            </TableCell>
                            <TableCell className="w-10 text-right">
                                <div className="flex justify-end">
                                    <Select
                                        align="end"
                                        trigger={
                                            <Button
                                                size="small"
                                                shape="square"
                                                intent="tertiary_deprecated"
                                                onClick={(e: { stopPropagation: () => any }) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <Icon
                                                    icon="moreHorizontal.medium"
                                                    className="text-gray-500"
                                                />
                                            </Button>
                                        }
                                        options={
                                            credential?.failingDate === undefined
                                                ? [
                                                      {
                                                          id: 'connect',
                                                          icon: 'checkmark',
                                                          title: 'Connect',
                                                      },
                                                  ]
                                                : [
                                                      {
                                                          id: 'disconnect',
                                                          icon: 'close',
                                                          title: 'Disconnect sync',
                                                          isDanger: true,
                                                      },
                                                  ]
                                        }
                                        onSelectOption={(option) => {
                                            if (option.id === 'connect') {
                                                startZoomAuth()
                                            }
                                            else if (option.id === 'disconnect') {
                                                disconnectAuth(credential)
                                            }
                                        }}
                                    />
                                </div>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <div className="flex items-center gap-x-4 font-bold">
                                    <PocketSuiteIcon
                                        style={{ maxWidth: '36px', maxHeight: '36px' }}
                                    />
                                    <Paragraph size="sm">PocketSuite</Paragraph>
                                </div>
                            </TableCell>
                            <TableCell>
                                <Tag
                                    label="Connected"
                                    intent="success"
                                    className="w-fit"
                                    size="sm"
                                />
                            </TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
export const SetupVideoConferencePage = usePSComponent(render, viewWillAppear)
